import React, { memo, useCallback } from 'react';
import { NodeResizer, type ResizeParams } from 'reactflow';
import PlotlyViewer from './PlotlyViewer';

// Custom styles for larger resize hit areas
const resizerStyles = `
  .react-flow__resize-control.line {
    border-color: transparent !important;
    transition: border-color 0.15s ease;
  }
  .react-flow__resize-control.line:hover {
    border-color: #60a5fa !important;
  }
  .react-flow__resize-control.line.left,
  .react-flow__resize-control.line.right {
    width: 12px !important;
    right: -6px;
  }
  .react-flow__resize-control.line.left {
    left: -6px !important;
    right: auto !important;
  }
  .react-flow__resize-control.line.right {
    right: -6px !important;
    left: auto !important;
  }
  .react-flow__resize-control.line.top,
  .react-flow__resize-control.line.bottom {
    height: 12px !important;
  }
  .react-flow__resize-control.line.top {
    top: -6px !important;
    bottom: auto !important;
  }
  .react-flow__resize-control.line.bottom {
    bottom: -6px !important;
    top: auto !important;
  }
  .react-flow__resize-control.handle {
    width: 16px !important;
    height: 16px !important;
  }
`;

interface PlotItem {
  file_name: string;
  file_type: string;
  plot_json?: string;
}

interface PlotNodeData {
  plot: PlotItem;
  imageUrl?: string;
  size: { width: number; height: number };
  onResize: (size: { width: number; height: number }) => void;
}

interface PlotNodeProps {
  data: PlotNodeData;
  selected?: boolean;
}

const PlotNode: React.FC<PlotNodeProps> = ({ data, selected }) => {
  const { plot, imageUrl, size, onResize } = data;

  const handleResizeEnd = useCallback(
    (_event: unknown, params: ResizeParams) => {
      onResize({ width: params.width, height: params.height });
    },
    [onResize]
  );

  // Extract a display name from the file name
  const displayName = plot.file_name
    .replace(/\.[^/.]+$/, '') // Remove extension
    .replace(/_/g, ' ') // Replace underscores with spaces
    .slice(0, 40); // Truncate if too long

  return (
    <>
      {/* Inject custom styles for larger resize hit areas */}
      <style>{resizerStyles}</style>

      {/* NodeResizer for edge and corner resizing - larger handles for easier grabbing */}
      <NodeResizer
        minWidth={300}
        minHeight={250}
        maxWidth={1000}
        maxHeight={800}
        isVisible={selected}
        lineClassName="!border-transparent hover:!border-blue-400 !border-4"
        handleClassName="!w-5 !h-5 !bg-white !border-2 !border-blue-400 !rounded"
        handleStyle={{ width: 16, height: 16 }}
        onResizeEnd={handleResizeEnd}
      />
      <div
        className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden cursor-grab active:cursor-grabbing"
        style={{ width: size.width, height: size.height }}
      >
        {/* Plot content - full height */}
        <div className="w-full h-full overflow-hidden">
          {plot.plot_json ? (
            <PlotlyViewer plotJson={plot.plot_json} fileName={displayName} />
          ) : imageUrl ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-50 p-2">
              <img
                src={imageUrl}
                alt={plot.file_name}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div class="flex items-center justify-center h-full">
                        <div class="text-center text-gray-500">
                          <div class="text-sm font-medium">Failed to load image</div>
                          <div class="text-xs mt-1">${plot.file_name}</div>
                        </div>
                      </div>
                    `;
                  }
                }}
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-500">
                <div className="text-sm font-medium">No plot data</div>
                <div className="text-xs mt-1">{plot.file_name}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(PlotNode);
